<template>
  <div class="container p-4 reporte-sync">
    <div class="flex flex-wrap align-items-center gap-2 mb-3">
      <ButtonPrime icon="pi pi-arrow-left" class="p-button-text" @click="router.push({ name: 'estudyoo-hub' })" />
      <h3 class="text-xl font-bold m-0">Reporte de sincronización</h3>
      <ButtonPrime
        label="Ir a sync"
        icon="pi pi-sync"
        class="p-button-sm p-button-outlined ml-auto"
        @click="router.push({ name: 'sync-django' })"
      />
    </div>

    <DjangoAuthGate @authenticated="onDjangoAuth" />

    <template v-if="djangoOk">
      <EstudyooLoadingBanner
        :visible="cargando && !colegios.length && !errorCarga"
        message="Generando reporte de sincronización…"
      />
      <div v-if="errorCarga" class="mb-3 p-3 border-round bg-red-50 text-sm text-red-800 border-1 border-red-200">
        {{ errorCarga }}
      </div>
      <div v-if="modoFallback" class="mb-3 p-3 border-round bg-orange-50 text-sm text-orange-900 border-1 border-orange-200">
        <strong>Modo estimado:</strong> el endpoint <code>admin/reporte-sync</code> no respondió correctamente.
        Los números se calculan desde las APIs de listado. Despliega la última versión del backend para el reporte completo (contratos, último sync, etc.).
      </div>
      <div
        v-if="!cargando && !errorCarga && !colegios.length && !(catalogo.capitulos || catalogo.ejes)"
        class="mb-3 p-3 border-round bg-blue-50 text-sm text-blue-900 border-1 border-blue-200"
      >
        No hay datos todavía. Conecta arriba, ejecuta un sync en
        <router-link :to="{ name: 'sync-django' }">Sincronizar desde legacy</router-link>
        y vuelve a cargar.
      </div>
      <div v-if="catalogoEnriquecido" class="mb-3 p-3 border-round bg-blue-50 text-sm text-blue-900 border-1 border-blue-200">
        Detalle de asignaturas calculado desde el listado de materias (el backend aún no expone la tabla en
        <code>catalogo_compartido.materias</code>). Despliega la última versión de
        <code>admin_reporte_sync.py</code> para obtenerlo nativo.
      </div>
      <CardPrime class="mb-4">
        <template #title>Catálogo compartido (todos los colegios)</template>
        <template #content>
          <p class="text-sm text-600 mb-3">{{ catalogo.nota }}</p>
          <div class="flex flex-wrap gap-3 mb-3 text-sm">
            <span><strong>{{ fmt(catalogo.ejes) }}</strong> ejes / unidades</span>
            <span><strong>{{ fmt(catalogo.capitulos) }}</strong> capítulos</span>
            <span v-if="catalogo.capitulos_sin_nivel" class="text-orange-600">
              {{ fmt(catalogo.capitulos_sin_nivel) }} sin nivel
            </span>
            <span><strong>{{ fmt(catalogo.actividades) }}</strong> actividades</span>
            <span><strong>{{ fmt(catalogo.materias_catalogo ?? catalogo.materias?.length) }}</strong> asignaturas en catálogo</span>
            <span v-if="catalogo.materias_globales" class="text-500">
              ({{ fmt(catalogo.materias_globales) }} sin colegio)
            </span>
            <span v-if="catalogo.materias_propias_colegio">
              · {{ fmt(catalogo.materias_propias_colegio) }} instancias por colegio
            </span>
          </div>
          <DataTable
            :value="catalogoMaterias"
            class="p-datatable-sm"
            :loading="cargando"
            scrollable
            scroll-height="280px"
            empty-message="Sin asignaturas en catálogo"
          >
            <Column field="nombre" header="Asignatura" />
            <Column field="codigo" header="Código" />
            <Column field="ejes" header="Ejes" style="width: 5rem" />
            <Column field="capitulos" header="Capítulos" style="width: 6rem" />
            <Column header="Colegios" style="width: 5.5rem">
              <template #body="{ data }">
                <span v-if="data.es_global" class="text-xs text-500">Global</span>
                <span v-else>{{ fmt(data.instancias_colegio || 0) }}</span>
              </template>
            </Column>
          </DataTable>
        </template>
      </CardPrime>

      <CardPrime>
        <template #title>
          <div class="flex flex-wrap align-items-center gap-2 w-full">
            <span>Datos por colegio</span>
            <TagPrime v-if="totales.colegios_sincronizados" severity="info" :value="`${totales.colegios_sincronizados} con espejo legacy`" />
            <ButtonPrime
              icon="pi pi-refresh"
              class="p-button-text p-button-sm ml-auto"
              :loading="cargando"
              @click="cargar"
            />
          </div>
        </template>
        <template #content>
          <p class="text-sm text-600 mb-3">
            Cursos, alumnos y contratos son <strong>por colegio</strong>.
            La columna <em>Espejo sync</em> cuenta registros legacy importados; las asignaturas sync reflejan materias vinculadas al colegio.
          </p>
          <DataTable
            v-model:expandedRows="expandedRows"
            :value="colegios"
            :loading="cargando"
            class="p-datatable-sm reporte-sync__tabla"
            data-key="id"
            scrollable
            scroll-height="520px"
            :global-filter-fields="['nombre', 'rbd', 'id_externo']"
            :filters="filters"
            filter-display="row"
            empty-message="No hay colegios en Django"
          >
            <Column expander style="width: 2.5rem" />
            <Column field="nombre" header="Colegio" sortable style="min-width: 10rem">
              <template #filter="{ filterModel, filterCallback }">
                <InputText v-model="filterModel.value" class="p-column-filter w-full" placeholder="Buscar" @input="filterCallback" />
              </template>
              <template #body="{ data }">
                <span>{{ data.nombre }}</span>
                <TagPrime v-if="!data.activo" severity="secondary" value="Inactivo" class="ml-1" />
              </template>
            </Column>
            <Column field="rbd" header="RBD" sortable style="width: 5rem" />
            <Column field="id_externo" header="ID legacy" sortable style="width: 5.5rem">
              <template #body="{ data }">
                <span v-if="data.id_externo" class="font-mono text-xs">{{ data.id_externo }}</span>
                <TagPrime v-else severity="warn" value="Sin sync" />
              </template>
            </Column>
            <Column header="Cursos" sortable sort-field="django.cursos" style="width: 4.5rem">
              <template #body="{ data }">{{ fmt(data.django.cursos) }}</template>
            </Column>
            <Column header="Alumnos" sortable sort-field="django.estudiantes_activos" style="width: 5rem">
              <template #body="{ data }">
                {{ fmt(data.django.estudiantes_activos) }}
                <span v-if="data.django.estudiantes > data.django.estudiantes_activos" class="text-xs text-500">
                  /{{ data.django.estudiantes }}
                </span>
              </template>
            </Column>
            <Column header="Contratos" sortable sort-field="django.contratos" style="width: 5rem">
              <template #body="{ data }">{{ fmt(data.django.contratos) }}</template>
            </Column>
            <Column header="Asignaturas sync" sortable sort-field="espejo_sync.asignaturas" style="width: 6rem">
              <template #body="{ data }">{{ fmt(data.espejo_sync.asignaturas) }}</template>
            </Column>
            <Column header="Asignaciones" sortable sort-field="django.asignaciones_activas" style="width: 5.5rem">
              <template #body="{ data }">
                {{ fmt(data.django.asignaciones_activas) }}
                <span v-if="data.django.asignaciones > data.django.asignaciones_activas" class="text-xs text-500">
                  /{{ data.django.asignaciones }}
                </span>
              </template>
            </Column>
            <Column header="Espejo sync" sortable sort-field="espejo_sync.total_registros" style="width: 5rem">
              <template #body="{ data }">
                <span :class="data.espejo_sync.total_registros ? 'text-green-700 font-semibold' : 'text-500'">
                  {{ fmt(data.espejo_sync.total_registros) }}
                </span>
              </template>
            </Column>
            <Column header="Último sync" style="min-width: 8rem">
              <template #body="{ data }">
                <span v-if="data.ultima_sesion?.finalizada_en" class="text-xs">
                  {{ fmtFecha(data.ultima_sesion.finalizada_en) }}
                </span>
                <span v-else class="text-500 text-xs">—</span>
              </template>
            </Column>
            <template #expansion="{ data }">
              <div class="p-3 pl-4 surface-50 border-round">
                <p class="text-sm font-semibold mb-2">Detalle espejo legacy ({{ data.nombre }})</p>
                <div class="flex flex-wrap gap-3 text-sm mb-3">
                  <span>Cursos sync: <strong>{{ data.espejo_sync.cursos }}</strong></span>
                  <span>Alumnos sync: <strong>{{ data.espejo_sync.estudiantes }}</strong></span>
                  <span>Contratos: <strong>{{ data.espejo_sync.contratos }}</strong></span>
                  <span>Det. contrato: <strong>{{ data.espejo_sync.detalles_contrato }}</strong></span>
                  <span>Asignaturas: <strong>{{ data.espejo_sync.asignaturas }}</strong></span>
                </div>
                <p v-if="data.ultima_sesion" class="text-xs text-600 mb-2">
                  Última sesión: {{ data.ultima_sesion.nombre }}
                </p>
                <template v-if="data.materias?.length">
                  <p class="text-sm font-semibold mb-1">Materias propias del colegio</p>
                  <DataTable :value="data.materias" class="p-datatable-sm">
                    <Column field="nombre" header="Materia" />
                    <Column field="codigo" header="Código" />
                    <Column field="ejes" header="Ejes" />
                    <Column field="capitulos" header="Capítulos" />
                  </DataTable>
                </template>
                <p v-else class="text-sm text-500 m-0">
                  Sin asignaturas sync registradas para este colegio.
                </p>
              </div>
            </template>
          </DataTable>
        </template>
      </CardPrime>
    </template>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { useToast } from 'primevue/usetoast';
import { FilterMatchMode } from 'primevue/api';
import ButtonPrime from 'primevue/button';
import CardPrime from 'primevue/card';
import Column from 'primevue/column';
import DataTable from 'primevue/datatable';
import InputText from 'primevue/inputtext';
import TagPrime from 'primevue/tag';
import DjangoAuthGate from '@/components/estudyoo/DjangoAuthGate.vue';
import EstudyooLoadingBanner from '@/components/estudyoo/EstudyooLoadingBanner.vue';
import { ActividadesApi } from '@/services/EstudyooAdminApi';
import { enriquecerCatalogoReporte, enriquecerColegiosMaterias } from '@/services/estudyooStatsFallback';
import { isDjangoAdminAuthenticated, handleDjangoApiError } from '@/services/djangoAdminAuth';

const router = useRouter();
const toast = useToast();
const djangoOk = ref(isDjangoAdminAuthenticated());
const cargando = ref(false);
const errorCarga = ref('');
const colegios = ref([]);
const catalogo = ref({ materias: [], nota: '' });
const totales = ref({});
const expandedRows = ref([]);
const filters = ref({
  nombre: { value: null, matchMode: FilterMatchMode.CONTAINS },
});
const modoFallback = ref(false);
const catalogoEnriquecido = ref(false);

function fmt(n) {
  if (n == null) return '0';
  return Number(n).toLocaleString('es-CL');
}

function fmtFecha(iso) {
  try {
    return new Date(iso).toLocaleString('es-CL', { dateStyle: 'short', timeStyle: 'short' });
  } catch {
    return iso;
  }
}

const catalogoMaterias = computed(() => catalogo.value.materias || []);

async function cargar() {
  cargando.value = true;
  errorCarga.value = '';
  catalogoEnriquecido.value = false;
  try {
    const data = await ActividadesApi.getReporteSync();
    let filas = data.colegios || [];
    filas = await enriquecerColegiosMaterias(filas);
    colegios.value = filas;
    const { catalogo: cat, enriquecido } = await enriquecerCatalogoReporte(
      data.catalogo_compartido || { materias: [] },
      filas,
    );
    catalogo.value = cat;
    catalogoEnriquecido.value = enriquecido;
    totales.value = data.totales || {};
    modoFallback.value = !!data._fallback;
    if (!colegios.value.length && modoFallback.value) {
      toast.add({
        severity: 'warn',
        summary: 'Sin colegios en Django',
        detail: 'Ejecuta sync legacy → Django o despliega el backend con admin/colegios.',
        life: 7000,
      });
    }
  } catch (e) {
    colegios.value = [];
    catalogo.value = { materias: [], nota: '' };
    totales.value = {};
    if (handleDjangoApiError(e, {
      toast,
      onUnauthorized: () => { djangoOk.value = false; },
    })) {
      errorCarga.value = 'Sesión Django expirada. Conecta de nuevo arriba.';
    } else {
      errorCarga.value = e.response?.data?.detail || e.message || 'No se pudo cargar el reporte.';
      toast.add({
        severity: 'error',
        summary: 'Reporte sync',
        detail: errorCarga.value,
        life: 7000,
      });
    }
  } finally {
    cargando.value = false;
  }
}

function onDjangoAuth(ok) {
  djangoOk.value = !!ok;
  if (ok) cargar();
}

onMounted(() => {
  if (djangoOk.value) cargar();
});
</script>

<style scoped>
.reporte-sync__tabla :deep(.p-column-filter) {
  width: 100%;
}
</style>
